import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LogoIcon } from '../components/Logo';
import {
  LayoutDashboard,
  Users,
  BarChart3,
  DollarSign,
  Settings,
  LogOut,
  Menu,
  ChevronRight,
  Package,
  CreditCard,
  Bot,
  Target,
  UserCog,
  ClipboardList,
  LifeBuoy,
} from 'lucide-react';
import { cn } from '../lib/utils';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/customers', label: 'Customers', icon: Users },
  { path: '/admin/agents', label: 'Agents', icon: Bot },
  { path: '/admin/leads', label: 'Leads (CRM)', icon: Target },
  { path: '/admin/users', label: 'User Management', icon: UserCog },
  { path: '/admin/analytics', label: 'Call Analytics', icon: BarChart3 },
  { path: '/admin/revenue', label: 'Revenue', icon: DollarSign },
  { path: '/admin/plans', label: 'Plans', icon: CreditCard },
  { path: '/admin/packages', label: 'Packages', icon: Package },
  { path: '/admin/agent-subscriptions', label: 'Subscriptions', icon: ClipboardList },
  { path: '/admin/support', label: 'Support', icon: LifeBuoy },
];

function AdminSidebar({ className }) {
  const location = useLocation();

  return (
    <nav className={cn("space-y-1", className)}>
      {navItems.map((item) => {
        const isActive = item.exact 
          ? location.pathname === item.path
          : location.pathname.startsWith(item.path);
        return (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200",
              isActive
                ? "bg-teal-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            )}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon className="w-5 h-5" />
            {item.label}
            {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Link>
        );
      })}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // Only redirect if we're done loading AND user is not an admin
    if (!loading && !isAdmin) {
      console.log('[AdminLayout] Redirecting to /admin/login - user:', user, 'isAdmin:', isAdmin);
      navigate('/admin/login', { replace: true });
    }
  }, [loading, isAdmin, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="admin-layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 flex-col">
        <div className="p-6 border-b border-slate-800">
          <Link to="/admin" className="flex items-center gap-3">
            <LogoIcon className="w-10 h-10" />
            <span className="text-xl font-jakarta font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Desqly</span>
          </Link>
          <p className="text-xs text-slate-500 mt-1">Admin Dashboard</p>
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          <AdminSidebar />
        </div>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-teal-600 text-white text-sm">
                {user?.name?.charAt(0) || 'A'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="lg:ml-64 min-h-screen bg-slate-50">
        {/* Top Header */}
        <header className="sticky top-0 z-40 h-16 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <div className="flex items-center gap-4">
              {/* Mobile Menu */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0 bg-slate-900 border-slate-800">
                  <div className="p-6 border-b border-slate-800">
                    <Link to="/admin" className="flex items-center gap-3">
                      <LogoIcon className="w-10 h-10" />
                      <span className="text-xl font-jakarta font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Desqly</span>
                    </Link>
                  </div>
                  <div className="p-4">
                    <AdminSidebar />
                  </div>
                </SheetContent>
              </Sheet>

              <h1 className="text-lg font-semibold text-slate-900 lg:hidden">Admin</h1>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" asChild>
                <Link to="/" target="_blank">View Site</Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="admin-user-menu">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {user?.name?.charAt(0) || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/admin/settings" className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="admin-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8 min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
